/**
 * Pan and zoom for the pixel-space SVG templates.
 *
 * A scatter of ~9,000 SKUs is unreadable at card size where the points pile up — the whole
 * low-velocity end is one dark mass. Zoom is what makes that legible without leaving the card.
 *
 * The transform is deliberately NOT an SVG `transform` on a group: that would scale the dots
 * and the type along with the geometry, which is exactly the distortion the measured-pixel
 * rendering exists to avoid. Callers map each base coordinate through `zx`/`zy` instead, so a
 * point moves and spreads while staying a 2px dot and a label stays 10px type.
 *
 * Interaction: ctrl/⌘+wheel zooms about the cursor (a trackpad pinch sends exactly this), drag
 * pans once zoomed, and the translation is clamped so content can never be dragged off its own
 * axes. A plain wheel is left alone — the chat rail and the dashboard both scroll. The corner
 * controls (`ZoomControls`) exist because the gesture on its own is not discoverable.
 */

import { type PointerEvent as ReactPointerEvent, useCallback, useRef, useState } from "react";

/** `screen = base * k + t`. `k === 1` with no translation is the unzoomed chart. */
export interface ChartZoom {
  k: number;
  tx: number;
  ty: number;
}

const IDENTITY: ChartZoom = { k: 1, tx: 0, ty: 0 };
const MAX_ZOOM = 24;
/** One notch of the +/− controls. */
const STEP = 1.6;

interface ZoomBox {
  width: number;
  height: number;
}

/** Keeps the drawing inside its own box: at `k` the content is `k` times the box, so the
 *  translation can run from `(1 - k) * size` (bottom/right edge flush) to 0 (top/left flush). */
function clampTranslation(zoom: ChartZoom, box: ZoomBox): ChartZoom {
  const tx = Math.min(0, Math.max((1 - zoom.k) * box.width, zoom.tx));
  const ty = Math.min(0, Math.max((1 - zoom.k) * box.height, zoom.ty));
  return { k: zoom.k, tx, ty };
}

/** Zoom to `nextK` while holding the point under (`cx`, `cy`) still. */
function zoomAbout(
  zoom: ChartZoom,
  nextK: number,
  cx: number,
  cy: number,
  box: ZoomBox,
): ChartZoom {
  const k = Math.min(MAX_ZOOM, Math.max(1, nextK));
  const ratio = k / zoom.k;
  return clampTranslation(
    { k, tx: cx - (cx - zoom.tx) * ratio, ty: cy - (cy - zoom.ty) * ratio },
    box,
  );
}

export interface ChartZoomControls {
  zoom: ChartZoom;
  zoomed: boolean;
  /** Base coordinate → on-screen coordinate. */
  zx: (x: number) => number;
  zy: (y: number) => number;
  zoomIn: () => void;
  zoomOut: () => void;
  reset: () => void;
  /** Spread onto the `<svg>`: wheel-zoom and drag-to-pan. Deliberately NO double-click-to-reset
   *  — a card's chart is also the click target that opens its popup, so a double-click there
   *  would reset the zoom and open the dialog at the same time. Reset is the corner button. */
  svgProps: {
    onWheel: (event: React.WheelEvent<SVGSVGElement>) => void;
    onPointerDown: (event: ReactPointerEvent<SVGSVGElement>) => void;
    onPointerMove: (event: ReactPointerEvent<SVGSVGElement>) => void;
    onPointerUp: (event: ReactPointerEvent<SVGSVGElement>) => void;
  };
  /** True while a pan is in progress — callers suppress hover work during a drag. */
  panning: boolean;
}

export function useChartZoom(box: ZoomBox): ChartZoomControls {
  const [zoom, setZoom] = useState<ChartZoom>(IDENTITY);
  const [panning, setPanning] = useState(false);
  const panFrom = useRef<{ x: number; y: number; tx: number; ty: number } | null>(null);

  const centre = { x: box.width / 2, y: box.height / 2 };

  const onWheel = useCallback(
    (event: React.WheelEvent<SVGSVGElement>) => {
      // Only the pinch/zoom gesture — a bare wheel belongs to whatever is scrolling.
      if (!event.ctrlKey && !event.metaKey) {
        return;
      }
      event.preventDefault();
      const rect = event.currentTarget.getBoundingClientRect();
      const cx = event.clientX - rect.left;
      const cy = event.clientY - rect.top;
      setZoom((current) => zoomAbout(current, current.k * Math.exp(-event.deltaY / 320), cx, cy, box)); // prettier-ignore
    },
    [box],
  );

  const onPointerDown = useCallback(
    (event: ReactPointerEvent<SVGSVGElement>) => {
      if (zoom.k <= 1 || event.button !== 0) {
        return;
      }
      panFrom.current = { x: event.clientX, y: event.clientY, tx: zoom.tx, ty: zoom.ty };
      setPanning(true);
      event.currentTarget.setPointerCapture(event.pointerId);
    },
    [zoom],
  );

  const onPointerMove = useCallback(
    (event: ReactPointerEvent<SVGSVGElement>) => {
      const from = panFrom.current;
      if (!from) {
        return;
      }
      setZoom((current) =>
        clampTranslation(
          {
            k: current.k,
            tx: from.tx + (event.clientX - from.x),
            ty: from.ty + (event.clientY - from.y),
          },
          box,
        ),
      );
    },
    [box],
  );

  const onPointerUp = useCallback((event: ReactPointerEvent<SVGSVGElement>) => {
    panFrom.current = null;
    setPanning(false);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  }, []);

  return {
    zoom,
    zoomed: zoom.k > 1,
    zx: (x: number) => x * zoom.k + zoom.tx,
    zy: (y: number) => y * zoom.k + zoom.ty,
    zoomIn: () => setZoom((current) => zoomAbout(current, current.k * STEP, centre.x, centre.y, box)), // prettier-ignore
    zoomOut: () => setZoom((current) => zoomAbout(current, current.k / STEP, centre.x, centre.y, box)), // prettier-ignore
    reset: () => setZoom(IDENTITY),
    svgProps: { onWheel, onPointerDown, onPointerMove, onPointerUp },
    panning,
  };
}
